import { useMutation } from '@tanstack/react-query';
import { changeLanguage } from '../adapters/i18next';
import type { SupportedLanguage } from '../config/languages';
import { getDateLocale } from '../core/date-locale';

/**
 * ロケール更新のために外部から注入される依存関係
 */
type LocaleApiClient = {
  PATCH: (url: '/v2/me/locale', options: {
    body: {
      locale: SupportedLanguage;
    };
  }) => Promise<unknown>;
};
type AuthStoreMethods = {
  setLocale: (locale: SupportedLanguage) => void;
  isAuthenticated: () => Promise<boolean>;
};
export interface LocaleUpdateDependencies {
  apiClient: LocaleApiClient;
  authStore?: AuthStoreMethods;
}

/**
 * ロケールを更新する
 */
export const updateLocale = async (newLocale: SupportedLanguage, {
  isAuthenticated,
  setLocale
}: {
  isAuthenticated: boolean;
  setLocale: (locale: SupportedLanguage) => void;
}, dependencies?: LocaleUpdateDependencies) => {
  try {
    if (isAuthenticated && dependencies?.apiClient) {
      await dependencies.apiClient.PATCH('/v2/me/locale', {
        body: {
          locale: newLocale
        }
      });
    }
    setLocale(newLocale);
    await changeLanguage(newLocale);
    const locale = await getDateLocale(newLocale);
    if (!locale) {
      throw new Error(`Failed to load locale: ${newLocale}`);
    }
  } catch (error) {
    console.error('Failed to update locale:', error);
    throw error;
  }
};

/**
 * ロケール更新のためのカスタムフック
 */
export function useUpdateLocale(dependencies?: LocaleUpdateDependencies) {
  const authMethods = dependencies?.authStore;
  return useMutation({
    mutationFn: async (newLocale: SupportedLanguage) => {
      if (!authMethods) {
        await changeLanguage(newLocale);
        const locale = await getDateLocale(newLocale);
        if (!locale) {
          throw new Error(`Failed to load locale: ${newLocale}`);
        }
        return;
      }
      await updateLocale(newLocale, {
        isAuthenticated: await authMethods.isAuthenticated(),
        setLocale: authMethods.setLocale
      }, dependencies);
    }
  });
}